import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/services/api/baseService';
import type {
  AnalysisSummary,
  CreatePipelineCasePayload,
  PipelineAttachment,
  PipelineCase,
  PipelineRequirement,
  PipelineStage,
  UpdatePipelineRequirementPayload,
  PipelineValueLine,
  ValueSummary,
} from '@/types/pipeline';

const BASE = '/api/crm/pipeline';

export const pipelineService = {
  getStages(): Promise<PipelineStage[]> {
    return apiGet<PipelineStage[]>(`${BASE}/stages`);
  },

  async listCases(params?: Record<string, string>) {
    const res = await apiGet<{ cases: PipelineCase[]; total: number }>(`${BASE}/cases`, { params });
    return res.cases || [];
  },

  getCase(id: number): Promise<PipelineCase> {
    return apiGet<PipelineCase>(`${BASE}/cases/${id}`);
  },

  createCase(data: CreatePipelineCasePayload): Promise<PipelineCase> {
    return apiPost<PipelineCase>(`${BASE}/cases`, data);
  },

  updateCase(id: number, data: Partial<CreatePipelineCasePayload> & { stage_id?: number }): Promise<PipelineCase> {
    return apiPut<PipelineCase>(`${BASE}/cases/${id}`, data);
  },

  updateStage(id: number, stageId: number): Promise<PipelineCase> {
    return apiPatch<PipelineCase>(`${BASE}/cases/${id}/stage`, { stage_id: stageId });
  },

  deleteCase(id: number): Promise<void> {
    return apiDelete(`${BASE}/cases/${id}`);
  },

  listAttachments(caseId: number): Promise<PipelineAttachment[]> {
    return apiGet<PipelineAttachment[]>(`${BASE}/cases/${caseId}/attachments`);
  },

  async uploadAttachment(caseId: number, file: File, documentType: string, description?: string): Promise<PipelineAttachment> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    if (description) formData.append('description', description);

    const response = await fetch(`/api/crm/pipeline/cases/${caseId}/attachments`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    const json = await response.json();
    if (!response.ok || !json.success) throw new Error(json.error || 'Upload failed');
    return json.data;
  },

  async uploadAttachments(
    caseId: number,
    files: File[],
    documentType: string,
    description?: string
  ): Promise<{ uploaded: PipelineAttachment[]; failed: { name: string; error: string }[] }> {
    const uploaded: PipelineAttachment[] = [];
    const failed: { name: string; error: string }[] = [];
    for (const file of files) {
      try {
        const att = await pipelineService.uploadAttachment(caseId, file, documentType, description);
        uploaded.push(att);
      } catch (err) {
        failed.push({ name: file.name, error: err instanceof Error ? err.message : 'Upload failed' });
      }
    }
    return { uploaded, failed };
  },

  getAttachmentFileUrl(caseId: number, attachmentId: number): string {
    return `/api/crm/pipeline/cases/${caseId}/attachments/${attachmentId}/file`;
  },

  deleteAttachment(caseId: number, attachmentId: number): Promise<void> {
    return apiDelete(`${BASE}/cases/${caseId}/attachments/${attachmentId}`);
  },

  listRequirements(caseId: number): Promise<{ requirements: PipelineRequirement[]; summary: AnalysisSummary }> {
    return apiGet<{ requirements: PipelineRequirement[]; summary: AnalysisSummary }>(`${BASE}/cases/${caseId}/requirements`);
  },

  startAnalysis(
    caseId: number,
    attachmentIds?: number[],
    options?: { force?: boolean }
  ): Promise<{ case_id: number; analysis_status: string }> {
    return apiPost(
      `${BASE}/cases/${caseId}/analyze`,
      {
        ...(attachmentIds ? { attachment_ids: attachmentIds } : {}),
        ...(options?.force ? { force: true } : {}),
      },
      { timeout: 30000 }
    );
  },

  updateRequirement(id: number, data: UpdatePipelineRequirementPayload): Promise<PipelineRequirement> {
    return apiPut<PipelineRequirement>(`${BASE}/requirements/${id}`, data);
  },

  createDevTask(requirementId: number, data?: { dev_project_id?: number; create_project?: boolean }) {
    return apiPost(`${BASE}/requirements/${requirementId}/dev-task`, data || {});
  },

  listDevProjects(): Promise<{ id: number; name: string }[]> {
    return apiGet<{ id: number; name: string }[]>(`${BASE}/dev-projects`);
  },

  getValueSummary(caseId: number): Promise<ValueSummary> {
    return apiGet<ValueSummary>(`${BASE}/cases/${caseId}/value-lines`);
  },

  addValueLine(caseId: number, data: Partial<PipelineValueLine> & { product_id?: string; product_name: string; pricing_type: string; quantity: number; unit_price: number }) {
    return apiPost<{ line: PipelineValueLine; summary: ValueSummary }>(`${BASE}/cases/${caseId}/value-lines`, data);
  },

  updateValueLine(caseId: number, lineId: number, data: Partial<PipelineValueLine>) {
    return apiPut<{ line: PipelineValueLine; summary: ValueSummary }>(`${BASE}/cases/${caseId}/value-lines/${lineId}`, data);
  },

  deleteValueLine(caseId: number, lineId: number): Promise<ValueSummary> {
    return apiDelete(`${BASE}/cases/${caseId}/value-lines/${lineId}`).then(() => pipelineService.getValueSummary(caseId));
  },
};
